import React from 'react';

// utils
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_LONG } from '../../util/types';
import { convertCategoriesToSelectTreeOptions, constructQueryParamName } from '../../util/search';

// component imports
import SelectSingleFilter from './SelectSingleFilter/SelectSingleFilter';
import SelectMultipleFilter from './SelectMultipleFilter/SelectMultipleFilter';
import BookingDateRangeFilter from './BookingDateRangeFilter/BookingDateRangeFilter';
import KeywordFilter from './KeywordFilter/KeywordFilter';
import PriceFilter from './PriceFilter/PriceFilter';
import IntegerRangeFilter from './IntegerRangeFilter/IntegerRangeFilter';
import SeatsFilter from './SeatsFilter/SeatsFilter';

/**
 * FilterComponent is used to map configured filter types
 * to actual filter components
 */
// console.debug('FILTER LABEL ->', config.key, config.label, config?.filterConfig?.label);
const FilterComponent = props => {
  const {
    idPrefix,
    config,
    urlQueryParams,
    initialValues,
    getHandleChangedValueFn,
    listingCategories,
    marketplaceCurrency,
    resetKey,
    intl,
    ...rest
  } = props;
  // Note: config can be either
  // - listingFields config or
  // - default filter config
  // They both have 'key' and 'schemaType' included.
  const { key, schemaType } = config;
  // --- RacketPort: Label ermitteln (filterConfig.label > config.label) und als i18n-Key formatieren
  const resolveLabel = cfg => {
    const raw = (cfg && cfg.filterConfig && cfg.filterConfig.label) || cfg.label || '';
    // Falls raw ein Übersetzungs-Key ist (z.B. "CustomFilters.shape"), löse ihn via intl auf.
    return typeof raw === 'string' ? intl.formatMessage({ id: raw, defaultMessage: raw }) : raw;
  };
  const { liveEdit, showAsPopup } = rest;

  const useHistoryPush = liveEdit || showAsPopup;
  const prefix = idPrefix || 'SearchPage';
  const componentId = `${prefix}.${key.toLowerCase()}`;
  const name = key.replace(/\s+/g, '-');
  const getAriaLabel = (label, values) => {
    const status = values ? 'active' : 'inactive';
    const mode = liveEdit ? 'live' : 'normal';
    return intl.formatMessage(
      { id: 'SearchPage.screenreader.openFilterButton' },
      { label, status, values, mode }
    );
  };
  const resolveOptionLabel = label => {
    if (typeof label !== 'string') {
      return label;
    }
    return label.includes('.') ? intl.formatMessage({ id: label, defaultMessage: label }) : label;
  };

  // Default filters: price, keywords, dates
  switch (schemaType) {
    case 'category': {
      const { scope, isNestedEnum, nestedParams } = config;
      const queryParamNames = nestedParams?.map(p => constructQueryParamName(p, scope)) || [
        constructQueryParamName(key, scope),
      ];
      return (
        <SelectSingleFilter
          id={componentId}
          name={key}
          label={intl.formatMessage({ id: 'FilterComponent.categoryLabel' })}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={convertCategoriesToSelectTreeOptions(listingCategories)}
          isNestedEnum={isNestedEnum}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    case 'listingType': {
      const { scope, options } = config;
      const paramNames = [constructQueryParamName(key, scope)];
      return (
        <SelectSingleFilter
          id={componentId}
          name={key}
          label={intl.formatMessage({ id: 'FilterComponent.listingTypeLabel' })}
          queryParamNames={paramNames}
          initialValues={initialValues(paramNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={options}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    case 'price': {
      const { min, max, step } = config;
      return (
        <PriceFilter
          id={componentId}
          name={key}
          resetKey={resetKey}
          label={
            config.label
              ? resolveLabel(config)
              : intl.formatMessage({ id: 'FilterComponent.priceLabel' })
          }
          description={config.description ? intl.formatMessage({ id: config.description }) : null}
          queryParamNames={[key]}
          initialValues={liveEdit ? { [key]: urlQueryParams[key] } : initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={min}
          max={max}
          step={step}
          marketplaceCurrency={marketplaceCurrency}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    case 'keywords':
      return (
        <KeywordFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.keywordsLabel' })}
          name={name}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    case 'dates': {
      const { dateRangeMode } = config;
      const isNightlyMode = dateRangeMode === 'night';
      return (
        <BookingDateRangeFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.datesLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          minimumNights={isNightlyMode ? 1 : 0}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    case 'seats': {
      return (
        <SeatsFilter
          id={componentId}
          name={name}
          label={intl.formatMessage({ id: 'FilterComponent.seatsLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
  }

  // Custom extended data filters
  switch (schemaType) {
    case SCHEMA_TYPE_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label: fcLabel, filterType } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      const resolvedEnumOptions = (enumOptions || []).map(option => ({
        ...option,
        label: resolveOptionLabel(option.label),
      }));
      const showMoreLabel = resolveOptionLabel(config.showMoreLabel);
      const showLessLabel = resolveOptionLabel(config.showLessLabel);

      // Label-Fallback: erst filterConfig.label, dann config.label,
      // wenn es wie ein i18n-Key aussieht → intl.formatMessage
      const rawLabel = fcLabel || config.label;
      const effectiveLabel =
        typeof rawLabel === 'string' && rawLabel.includes('.')
          ? intl.formatMessage({ id: rawLabel })
          : rawLabel || key;

      // Wenn filterType fehlt, nimm bei ENUM standardmäßig Single
      const isSingle = filterType ? filterType === 'SelectSingleFilter' : true;

      return isSingle ? (
        <SelectSingleFilter
          id={componentId}
          label={effectiveLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={resolvedEnumOptions}
          priorityOptionKeys={config.priorityOptionKeys}
          showMoreLabel={showMoreLabel}
          showLessLabel={showLessLabel}
          isNestedEnum={false}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      ) : (
        <SelectMultipleFilter
          id={componentId}
          label={effectiveLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={resolvedEnumOptions}
          priorityOptionKeys={config.priorityOptionKeys}
          showMoreLabel={showMoreLabel}
          showLessLabel={showLessLabel}
          schemaType={schemaType}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_MULTI_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label: fcLabel, searchMode } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      const resolvedEnumOptions = (enumOptions || []).map(option => ({
        ...option,
        label: resolveOptionLabel(option.label),
      }));

      const rawLabel = fcLabel || config.label;
      const effectiveLabel =
        typeof rawLabel === 'string' && rawLabel.includes('.')
          ? intl.formatMessage({ id: rawLabel })
          : rawLabel || key;

      return (
        <SelectMultipleFilter
          id={componentId}
          label={effectiveLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={resolvedEnumOptions}
          schemaType={schemaType}
          searchMode={searchMode}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_LONG: {
      const { minimum, maximum, scope, step, filterConfig = {} } = config;
      const label = resolveLabel(config);
      const queryParamNames = [constructQueryParamName(key, scope)];
      return (
        <IntegerRangeFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={minimum}
          max={maximum}
          step={step}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    default:
      return null;
  }
};

export default FilterComponent;
